/**
 * Pixel-level DOM recorder backed by rrweb. Lazy-loaded so the heavy
 * recorder bundle (~60KB minified) doesn't ship for tenants that haven't
 * opted into `replayConfig.captureDom`. Posts batches to
 * `POST /v1/replay/dom-events` with the same risk-session token the
 * anchored recorder uses.
 *
 * Privacy guards (enabled by default — no per-tenant config):
 *   - `maskAllInputs: true`           — every <input>/<textarea>/<select>
 *     value is replaced with `*` characters before serialization.
 *   - `blockClass: 'upg-no-record'`   — any element tagged with this class
 *     is omitted from the captured DOM entirely.
 *   - `maskTextClass: 'upg-mask-text'` — text content masked.
 *   - `collectFonts: false`           — no font slurping.
 *
 * Fidelity: `inlineStylesheet: true` embeds the checkout's OWN (same-origin) CSS
 * into snapshots so the admin replay renders styled under its CSP (see
 * REPLAY_RECORD_OPTIONS). Same-origin only — no third-party CSS is slurped.
 *
 * The recorder runs independently of the anchored client; both share the
 * `riskSessionToken` for auth.
 */
import { baseUrl } from './api.js';
import type { ReplayConfigClientHint } from './api.js';
import { classifyFlushResult } from './dom-recorder-policy.js';

type Disposer = () => void;
type RrwebEvent = unknown; // opaque to us — we serialise and forward as-is

interface RecorderState {
  sessionId: string;
  token: string;
  buffer: RrwebEvent[];
  stop: Disposer | null;
  timer: ReturnType<typeof setInterval> | null;
  closed: boolean;
  /**
   * bd vc8u — consecutive network-error retries on flushBatch. Capped by
   * MAX_FLUSH_FAILURES via classifyFlushResult so a flaky connection can't
   * grow the buffer unboundedly across a long session.
   */
  consecutiveFailures: number;
}

/** bd vc8u — drop the batch after this many consecutive network errors. */
const MAX_FLUSH_FAILURES = 5;

let state: RecorderState | null = null;

// Tight enough that even a 2-second session lands at least one batch on
// the server before the shopper navigates to the provider HPP. Combined
// with the explicit flush before window.location = redirect_url, the
// "session too brief" failure mode is gone.
const FLUSH_INTERVAL_MS = 2000;
// Per-batch upload size on the wire. Server caps at 2 MB, but we batch
// smaller so the initial rrweb DOM snapshot (often 300–800 KB on its own)
// goes up in its own request rather than blocking subsequent events.
const BATCH_BYTE_CAP = 512 * 1024;

/**
 * Static rrweb capture options (privacy + fidelity). The per-session `emit`
 * callback is added at record() time.
 *
 * `inlineStylesheet: true` embeds the checkout's own (same-origin) CSS into each
 * full snapshot, instead of leaving a `<link href="https://pay.<env>/…/index-*.css">`
 * the admin replayer would have to fetch CROSS-ORIGIN. The admin app's CSP
 * (`style-src 'self' 'unsafe-inline' …`) blocks that cross-origin stylesheet, so
 * with link-refs the replay renders UNSTYLED (a bare <button> shows as plain
 * text). Inlined styles replay under the existing `'unsafe-inline'` allowance —
 * no CSP change, and faithful even after the checkout app redeploys/rotates its
 * asset hash. Exported so the setting is unit-pinned against regression.
 */
export const REPLAY_RECORD_OPTIONS = {
  maskAllInputs: true,
  blockClass: 'upg-no-record',
  maskTextClass: 'upg-mask-text',
  collectFonts: false,
  inlineStylesheet: true,
  recordCanvas: false,
} as const;

/**
 * Start the DOM recorder. Idempotent — calling twice is a no-op. The
 * `replay` arg comes straight from `CheckoutConfigDto.tenant.replay`; the
 * recorder self-disables when it's off so callers can fire-and-forget.
 */
export async function startDomRecorder(args: {
  sessionId: string;
  token: string;
  replay: ReplayConfigClientHint;
}): Promise<void> {
  if (state) return;
  if (!args.replay.enabled || !args.replay.captureDom) return;
  if (!args.token || !args.sessionId) return;

  // Lazy import keeps the 60KB rrweb code out of the initial bundle for
  // tenants who haven't turned the toggle on. Falls back gracefully if the
  // dep is missing at runtime — production builds should always have it.
  let record: (opts: Record<string, unknown>) => Disposer;
  try {
    const mod = (await import('rrweb')) as { record: typeof record };
    record = mod.record;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('rrweb unavailable — DOM replay disabled for this session', err);
    return;
  }

  const local: RecorderState = {
    sessionId: args.sessionId,
    token: args.token,
    buffer: [],
    stop: null,
    timer: null,
    closed: false,
    consecutiveFailures: 0,
  };
  state = local;

  const stop = record({
    emit: (event: RrwebEvent) => {
      if (local.closed) return;
      local.buffer.push(event);
      if (estimateBytes(local.buffer) >= BATCH_BYTE_CAP) {
        void flushBatch(local);
      }
    },
    ...REPLAY_RECORD_OPTIONS,
  });
  local.stop = stop;
  local.timer = setInterval(() => {
    if (local.buffer.length > 0) void flushBatch(local);
  }, FLUSH_INTERVAL_MS);

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', onPageHide);
  }
}

/**
 * Force-flush any buffered events without stopping the recorder. Call this
 * right before navigating away (e.g., `window.location = redirect_url`) so
 * the tail of the session lands on the server. Idempotent + safe to call
 * when the recorder isn't running.
 */
export async function flushDomRecorder(): Promise<void> {
  if (state) await flushBatch(state);
}

/** Flush + stop. Called on unmount + on pagehide. */
export async function stopDomRecorder(): Promise<void> {
  const local = state;
  if (!local || local.closed) return;
  local.closed = true;
  if (local.timer) {
    clearInterval(local.timer);
    local.timer = null;
  }
  if (local.stop) {
    try {
      local.stop();
    } catch {
      /* rrweb's stop is best-effort */
    }
    local.stop = null;
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('pagehide', onPageHide);
  }
  await flushBatch(local);
  state = null;
}

async function flushBatch(local: RecorderState): Promise<void> {
  if (local.buffer.length === 0) return;
  const events = local.buffer;
  local.buffer = [];
  const body = JSON.stringify({
    session_id: local.sessionId,
    events,
    seq: Date.now(),
  });

  try {
    const useBeacon =
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden';
    if (useBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(`/v1/replay/dom-events?token=${encodeURIComponent(local.token)}`, blob);
      return;
    }
    let networkError = false;
    let httpOk = false;
    let status = 0;
    try {
      const res = await fetch(`${baseUrl}/v1/replay/dom-events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Replay-Token': local.token,
        },
        credentials: 'include',
        body,
        keepalive: true,
      });
      httpOk = res.ok;
      status = res.status;
    } catch {
      networkError = true;
    }

    const decision = classifyFlushResult({
      networkError,
      httpOk,
      consecutiveFailures: local.consecutiveFailures,
      maxFailures: MAX_FLUSH_FAILURES,
    });
    local.consecutiveFailures = decision.nextFailures;

    if (decision.action === 'requeue') {
      // bd vc8u — bounded re-queue. Server is idempotent on (session, seq).
      local.buffer.unshift(...events);
    } else if (decision.action === 'drop' && !httpOk && !networkError) {
      // eslint-disable-next-line no-console
      console.warn(
        `replay.dom_events.rejected status=${status} bytes=${body.length} events=${events.length}`,
      );
    }
  } catch {
    // Outer catch retained for any non-fetch throw (shouldn't happen, but
    // belt-and-suspenders so an exception here never blows up the recorder
    // tick).
  }
}

function onVisibilityChange(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    if (state) void flushBatch(state);
  }
}

function onPageHide(): void {
  void stopDomRecorder();
}

/**
 * Rough byte estimate without serialising twice. We just need a flush
 * threshold — exact accuracy doesn't matter, only "is the buffer huge?".
 */
function estimateBytes(events: RrwebEvent[]): number {
  let total = 0;
  for (const ev of events) {
    try {
      total += JSON.stringify(ev).length;
    } catch {
      total += 200;
    }
  }
  return total;
}
